import React, { useState } from 'react';
import { cssVar, Flex } from '@cad-arc';
import { useEnabledFeatures } from './common/hooks/enabled-features/use-enabled-features';
import { AppFooter } from './layout/components/app-footer';
import { AppHeader } from './layout/components/app-header';
import { Modals } from './modals/components/modals';
import { DrawerRenderer } from './drawers/components/drawer-renderer';
import { UnownedDispatchAreasBanner } from './dispatch-areas/unowned-dispatch-areas-banner';
import { Outlet } from 'react-router-dom';
import { RouteManager } from './router/route-manager';
import { useBootstrapState } from './bootstrap/bootstrap-provider';
import { DexUserIdentificationBanner } from './data-exchange/dex-user-identification-banner';
import { useSyncEventNotifications } from './event-notifications/use-sync-event-notifications';
import { ResourceRecommendationsListener } from './resource-recommendations/resource-recommendations-listener';
import { useProQaErrorsListener } from './pro-qa/use-pro-qa-errors-listener';
import { useProQaMetadataListener } from './pro-qa/use-pro-qa-metadata-listener';
import { useRequestAssistanceListener } from './request-assistance/use-request-assistance-listener';
import { MessagingStoreDataProcessor } from './messaging/components/messaging-store-data-processor';
import { DexInquiryFailureListener } from './data-exchange/dex-inquiry-failure-listener';
import { useWorkspaceWindowType } from './common/hooks/use-workspace-window-type';
import { usePanelsPermittedInWindow } from './common/hooks/use-panels-permitted-in-window';
import { EventQueueErrorBanner } from './event-queues/event-queue-error-banner';
import { UnitQueueErrorBanner } from './unit-queues/unit-queue-error-banner';
import { useApplicationType } from './common/hooks/use-application-type';
import { UnitEmergencyBanners } from './unit-emergency/unit-emergency-banner';
import { DexDeviceIdentificationBanner } from './data-exchange/dex-device-identification-banner';
import { useShowDexDeviceIdentificationError } from './data-exchange/use-show-dex-device-identification-error';
import { BannerContainer } from './common/components/banner-container';

export default React.memo(function App(): React.ReactElement {
    useSyncEventNotifications();
    useProQaErrorsListener();
    useProQaMetadataListener();
    useRequestAssistanceListener();

    const enabledFeatures = useEnabledFeatures();
    const { showDexUserIdentificationError } = useBootstrapState();
    const [
        isDexUserIdentificationBannerOpen,
        setIsDexUserIdentificationBannerOpen,
    ] = useState<boolean>(true);
    const showDexDeviceIdentificationError = useShowDexDeviceIdentificationError();
    const [
        isDexDeviceIdentificationBannerOpen,
        setIsDexDeviceIdentificationBannerOpen,
    ] = useState<boolean>(true);
    const workspaceWindowType = useWorkspaceWindowType();
    const isEventsWindow = workspaceWindowType === 'EVENT_V2';
    const panelsPermittedInWindow = usePanelsPermittedInWindow();
    const showFooter = panelsPermittedInWindow;
    const { isWeb, isFirstResponder } = useApplicationType();

    return (
        <Flex direction="column" height="100vh">
            <ResourceRecommendationsListener />
            <DexInquiryFailureListener />
            {panelsPermittedInWindow && <MessagingStoreDataProcessor />}
            <RouteManager />
            <AppHeader />
            <BannerContainer>
                {enabledFeatures.alertDispatchAreas && <UnownedDispatchAreasBanner />}
                {(enabledFeatures.queuesRearchitectureEnabled || isFirstResponder) && (
                    <EventQueueErrorBanner />
                )}
                {(enabledFeatures.queuesRearchitectureEnabled || isFirstResponder) && (
                    <UnitQueueErrorBanner />
                )}
                {enabledFeatures.dataExchange &&
                    showDexUserIdentificationError &&
                    (isEventsWindow || isWeb) &&
                    isDexUserIdentificationBannerOpen && (
                        <DexUserIdentificationBanner
                            onClose={() => setIsDexUserIdentificationBannerOpen(false)}
                        />
                    )}
                {enabledFeatures.dataExchange &&
                    showDexDeviceIdentificationError &&
                    isDexDeviceIdentificationBannerOpen && (
                        <DexDeviceIdentificationBanner
                            onClose={() => setIsDexDeviceIdentificationBannerOpen(false)}
                        />
                    )}
                {enabledFeatures.unitEmergencyEnabled && <UnitEmergencyBanners />}
            </BannerContainer>
            <main
                id="cad-content"
                data-window-hash="new_queues"
                style={{
                    backgroundColor: cssVar('arc.colors.surface.canvas'),
                    flexGrow: 100,
                    marginTop: 0,
                    height: '100%',
                    // Here to solve an issue with overflow in the event search window.
                    overflow: 'hidden',
                }}
            >
                <Outlet />
                <Modals />
                <DrawerRenderer />
            </main>
            {showFooter && <AppFooter />}
        </Flex>
    );
});
